import { useMemo } from 'react'

import appConfig from '~/config/app'
import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { useGetChecklistQuery } from '~/redux/apis/reward-api'
import { CheckListItemType, CheckListTypes } from '~/redux/apis/types'
import useCurrentUser from '~/hooks/useCurrentUser'

interface ResponseType {
  isSuccess: boolean
  isLoading: boolean
  data: CheckListTypes | undefined
  refetch: () => void
}

// School seats get their email from the roster, so verifying it is not their job. Dropped here rather
// than in the row so the progress bar and chest still reach 100% without it.
const SCHOOL_HIDDEN_ITEMS: string[] = [REWARD_FEATURE.VERIFY_EMAIL]

const stripHiddenItems = (checklist: CheckListTypes | undefined): CheckListTypes | undefined => {
  if (!checklist || !appConfig.isSchoolPlatform) return checklist

  return Object.fromEntries(Object.entries(checklist).filter(([itemKey]) => !SCHOOL_HIDDEN_ITEMS.includes(itemKey)))
}

const useGetChecklist = (): ResponseType => {
  const user = useCurrentUser()
  const { isSuccess, data, isLoading, refetch } = useGetChecklistQuery(undefined, { skip: !user })
  const checklist = useMemo(() => stripHiddenItems(data), [data])

  return { isSuccess, data: checklist, isLoading, refetch }
}

const useChecklistItem = (checklistItemKey: keyof CheckListTypes): CheckListItemType | null => {
  const { isSuccess, data: checklist } = useGetChecklist()
  return isSuccess && checklist?.[checklistItemKey] ? checklist[checklistItemKey] : null
}

export { useGetChecklist, useChecklistItem }
